import React from 'react';
import {
	Box,
	Typography,
	Grid,
	TextField,
	Checkbox,
	FormControlLabel,
	CircularProgress,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { getDays } from 'core/API/organizationSettings';
import { getShiftRequests } from 'core/API/shift-requests';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useAuth } from 'module/auth/context/AuthContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { UserAvailability } from 'core/interfaces/userInterface';
import { apiService } from 'core/services/apiService';
import { ShiftRequest } from 'types/shift-requests';
import CurrentAvailabilityList from '../../CurrentAvailabilityList/CurrentAvailabilityList';
import {
	buildAllWeekDays,
	collectRelatedAvailabilityRequests,
	findAvailabilityRequestForDay,
	getLatestAvailabilityForDay,
	getRequestAvailabilityTimes,
	toDayjsAvailabilityTime,
} from '../../../utils/availabilityDisplay';

interface AvailabilityFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	formRef?: React.MutableRefObject<any>;
	userId?: number;
	editRequest?: ShiftRequest | null;
}

const validationSchema = Yup.object({
	availability: Yup.array(),
	notes: Yup.string().required('Reason for change is required'),
});

/** True when weekly availability day/time selections differ from the initial snapshot. */
export const hasAvailabilityDayChanges = (
	currentAvailability: any[] | undefined,
	initialAvailability: any[] | undefined,
): boolean => {
	if (!currentAvailability?.length) return false;
	return currentAvailability.some((d: any, index: number) => {
		const initial = initialAvailability?.[index];
		if (!initial) return d.enabled;

		if (d.enabled !== initial.enabled) return true;

		if (d.enabled) {
			const startChanged =
				d.startTime && initial.startTime
					? !dayjs(d.startTime).isSame(dayjs(initial.startTime), 'minute')
					: d.startTime !== initial.startTime;
			const endChanged =
				d.endTime && initial.endTime
					? !dayjs(d.endTime).isSame(dayjs(initial.endTime), 'minute')
					: d.endTime !== initial.endTime;
			return startChanged || endChanged;
		}

		return false;
	});
};

const AvailabilityForm: React.FC<AvailabilityFormProps> = ({
	onSubmit,
	onCancel: _onCancel,
	formRef,
	userId,
	editRequest,
}) => {
	const { showToast } = useToastContext();
	const { workplaceId } = useWorkplacesContext();
	const { user } = useAuth();
	const currentUserId = user?.id || parseInt(localStorage.getItem('userId') || '0');

	// Fetch days for availability picker
	const { data: daysData, isLoading: daysLoading } = useQuery(['days'], () => getDays());
	
	const allDays = React.useMemo(() => buildAllWeekDays(daysData), [daysData]);
	
	// Fetch user's data with availability (refetch on mount to ensure fresh data)
	// Use userId prop if provided (from user settings), otherwise use current user
	const targetUserId = userId || currentUserId;
	const { data: userData, isLoading: userLoading } = useQuery(
		['user-availability', targetUserId],
		async () => {
			const endpoint = userId ? `/users/${userId}` : '/users/me';
			const response = await apiService.get(endpoint);
			return apiService.responseHandler(response);
		},
		{
			enabled: !!targetUserId,
			refetchOnMount: 'always', // Always refetch when component mounts
			staleTime: 0, // Consider data stale immediately
		}
	);
	
	// Get user's current availability from the fresh query data
	const currentAvailability: UserAvailability[] = React.useMemo(
		() => userData?.userAvailability || user?.userAvailability || [],
		[userData?.userAvailability, user?.userAvailability]
	);

	const { data: shiftRequestsData, isLoading: relatedRequestsLoading } = useQuery(
		['shift-requests', workplaceId],
		() => getShiftRequests({ workplace_id: workplaceId }),
		{
			enabled: !!editRequest && !!workplaceId,
		}
	);

	const relatedAvailabilityRequests = React.useMemo(() => {
		if (!editRequest) return [];
		return collectRelatedAvailabilityRequests(shiftRequestsData, editRequest);
	}, [editRequest, shiftRequestsData]);

	// Store initial values in a ref to compare for changes
	const initialValuesRef = React.useRef<any>(null);

	const handleSubmit = async (values: any) => {
		// Validate that at least one day is selected
		const enabledDays = values.availability.filter((d: any) => d.enabled);

		if (enabledDays.length === 0) {
			const message = 'Please select at least one day';
			showToast('error', message);
			const error = new Error(message) as Error & { isFormValidation?: boolean };
			error.isFormValidation = true;
			throw error;
		}

		// Validate that all enabled days have start and end times
		const invalidDays = values.availability.filter(
			(d: any) => d.enabled && (!d.startTime || !d.endTime)
		);

		if (invalidDays.length > 0) {
			const dayNames = invalidDays.map((d: any) => d.day.label).join(', ');
			const message = `Please set start and end times for: ${dayNames}`;
			showToast('error', message);
			const error = new Error(message) as Error & { isFormValidation?: boolean };
			error.isFormValidation = true;
			throw error;
		}

		// Only send requests for days that have changed
		const changedDays = values.availability.filter((d: any, index: number) => {
			const initial = initialValuesRef.current?.availability;
			return hasAvailabilityDayChanges([d], initial ? [initial[index]] : undefined);
		});

		if (changedDays.length === 0) {
			return;
		}

		// Transform the data to match the expected format (only changed days)
		// Include both enabled days (with times) and disabled days (with null times)
		// to properly handle unsetting/removing availability
		const availabilityRequests = changedDays.map((d: any) => ({
			day: d.day.id,
			startTime: d.enabled && d.startTime ? dayjs(d.startTime).format('HH:mm:ss') : null,
			endTime: d.enabled && d.endTime ? dayjs(d.endTime).format('HH:mm:ss') : null,
			requestId: d.requestId || null,
		}));

		await onSubmit({
			availabilityRequests,
			notes: values.notes,
		});

		// Keep change detection in sync so a second Save does not re-post the same days
		initialValuesRef.current = JSON.parse(JSON.stringify(values));
	};

	const initialValues = React.useMemo(
		() => ({
			availability: allDays.map((day) => {
				if (editRequest) {
					const dayRequest = findAvailabilityRequestForDay(relatedAvailabilityRequests, day);
					const { startValue, endValue, enabled } = getRequestAvailabilityTimes(dayRequest);

					return {
						day,
						enabled,
						startTime: enabled ? toDayjsAvailabilityTime(startValue) : null,
						endTime: enabled ? toDayjsAvailabilityTime(endValue) : null,
						requestId: dayRequest?.id ?? null,
					};
				}

				const latestAvail = getLatestAvailabilityForDay(currentAvailability, day);

				if (latestAvail) {
					// Stored rows are available days (incl. 24h equal-clock windows).
					const startTime = toDayjsAvailabilityTime(latestAvail.startAt);
					const endTime = toDayjsAvailabilityTime(latestAvail.endAt);

					return {
						day,
						enabled: Boolean(startTime && endTime),
						startTime,
						endTime,
						requestId: null,
					};
				}

				return {
					day,
					enabled: false,
					startTime: null,
					endTime: null,
					requestId: null,
				};
			}),
			notes:
				editRequest?.note ||
				(currentAvailability.length > 0 ? 'Updating existing availability' : ''),
		}),
		[allDays, currentAvailability, editRequest, relatedAvailabilityRequests]
	);

	// Store initial values for comparison
	React.useEffect(() => {
		const stillLoading = daysLoading || userLoading || (!!editRequest && relatedRequestsLoading);
		if (stillLoading || initialValues.availability.length === 0) return;
		initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));
	}, [initialValues, daysLoading, userLoading, editRequest, relatedRequestsLoading]);

	const setFormikRef = React.useCallback(
		(instance: any) => {
			if (!formRef) return;
			if (!instance) {
				formRef.current = null;
				return;
			}
			formRef.current = {
				...instance,
				hasAvailabilityChanges: () =>
					hasAvailabilityDayChanges(
						instance.values?.availability,
						initialValuesRef.current?.availability,
					),
			};
		},
		[formRef],
	);

	return (
		<Formik
			innerRef={setFormikRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, setFieldValue, errors, touched }) => (
				<Form>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box sx={{ mt: 3 }}>
							{daysLoading || userLoading || (!!editRequest && relatedRequestsLoading) ? (
								<Box
									sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}
								>
									<CircularProgress size={20} color="inherit" />
									<Typography sx={{ ml: 2 }}>Loading availability options...</Typography>
								</Box>
							) : (
								<>
									<CurrentAvailabilityList
										days={allDays}
										currentAvailability={currentAvailability}
									/>

									<Typography variant="h6" gutterBottom>
										Update Your Availability
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
										Select the days you are available and specify your preferred time range.
									</Typography>

									{values.availability.map((d: any, index: number) => (
										<Grid
											container
											spacing={2}
											alignItems="center"
											key={d.day['@id']}
											sx={{
												mb: 1,
												borderRadius: 1,
												p: 1,
											}}
										>
											<Grid item xs={4}>
												<FormControlLabel
													control={
														<Checkbox
															checked={d.enabled}
															onChange={(e) => {
																const updated = [...values.availability];
																updated[index].enabled = e.target.checked;

																if (!e.target.checked) {
																	updated[index].startTime = null;
																	updated[index].endTime = null;
																} else {
																	const lastFilledDay = updated
																		.slice(0, index)
																		.reverse()
																		.find(
																			(day: any) => day.enabled && day.startTime && day.endTime
																		);

																	if (lastFilledDay) {
																		updated[index].startTime = dayjs(lastFilledDay.startTime);
																		updated[index].endTime = dayjs(lastFilledDay.endTime);
																	} else {
																		// showToast('success', `Please set start and end times for ${updated[index].day.label}`);
																	}
																}
																setFieldValue('availability', updated);
															}}
														/>
													}
													label={d.day.label}
												/>
											</Grid>

											<Grid item xs={4}>
												<TimePicker
													label="Start Time"
													ampm
													format="hh:mm A"
													value={d.startTime}
													onChange={(newTime) => {
														const updated = [...values.availability];
														updated[index].startTime = newTime;
														setFieldValue('availability', updated);
													}}
													disabled={!d.enabled}
													slotProps={{
														textField: {
															fullWidth: true,
														},
													}}
												/>
											</Grid>

											<Grid item xs={4}>
												<TimePicker
													label="End Time"
													ampm
													format="hh:mm A"
													value={d.endTime}
													onChange={(newTime) => {
														const updated = [...values.availability];
														updated[index].endTime = newTime;
														setFieldValue('availability', updated);
													}}
													disabled={!d.enabled}
													slotProps={{
														textField: {
															fullWidth: true,
														},
													}}
												/>
											</Grid>
										</Grid>
									))}

									<TextField
										fullWidth
										multiline
										rows={3}
										label="Reason for Change"
										value={values.notes}
										onChange={(e) => setFieldValue('notes', e.target.value)}
										required
										error={touched.notes && Boolean(errors.notes)}
										helperText={touched.notes && errors.notes}
										sx={{ mt: 2 }}
									/>
								</>
							)}
						</Box>
					</LocalizationProvider>
				</Form>
			)}
		</Formik>
	);
};

export default AvailabilityForm;
